// src/hooks/useAuth.jsx
import { useState, createContext, useContext, useEffect, useMemo } from 'react';
import { authAPI } from '../api/auth';
import { profilesService } from '../api/profiles';
import { TokenManager } from '../api/interceptors';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe usarse dentro de AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        initializeAuth();
    }, []);

    const initializeAuth = async () => {
        const token = TokenManager.getToken();
        const savedUser = TokenManager.getUser();

        //console.log('🔄 Inicializando autenticación...');
        //console.log('🔑 Token en inicialización:', token);
        //console.log('👤 Usuario guardado:', savedUser);

        if (token && savedUser) {
            try {
                // Verificar si el token es válido
                const isValid = await authAPI.verifyToken(token);
                if (isValid) {
                    setUser(savedUser);
                    //console.log('✅ Usuario cargado desde localStorage');
                } else {
                    //console.log('🔄 Token inválido, intentando refresh...');
                    await handleTokenRefresh();
                }
            } catch (error) {
                //console.error('❌ Error verificando token:', error);
                logout();
            }
        }
        setLoading(false);
    };

    const handleTokenRefresh = async () => {
        const refreshToken = TokenManager.getRefreshToken();
        if (!refreshToken) {
            //console.log('❌ No hay refresh token disponible');
            logout();
            return;
        }

        try {
            const tokens = await authAPI.refreshToken(refreshToken);
            TokenManager.setToken(tokens.access);
      
            // Recargar datos del usuario con el nuevo token
            const userProfile = await profilesService.getMyProfile();
            setUser(userProfile);
            TokenManager.setUser(userProfile);
            //console.log('✅ Token refresh exitoso');
        } catch (refreshError) {
            //console.error('❌ Token refresh failed:', refreshError);
            logout();
        }
    };

    const login = async (credentials) => {
        setLoading(true);
        setError(null);
        try {
            //console.log('🔐 Intentando login con:', credentials);
            
            const tokens = await authAPI.login(credentials);
            //console.log('✅ Login exitoso, tokens recibidos:', tokens);
            
            // ✅ CORREGIDO: Guardar tokens inmediatamente
            TokenManager.setToken(tokens.access);
            TokenManager.setRefreshToken(tokens.refresh);

            //console.log('📋 Obteniendo datos del usuario...');
            
            // ✅ CORREGIDO: Usar el token recién guardado para obtener el perfil
            const userProfile = await profilesService.getMyProfile();
            //console.log('✅ Datos de usuario recibidos:', userProfile);
      
            setUser(userProfile);
            TokenManager.setUser(userProfile);

            return tokens;
        } catch (error) {
            //console.error('❌ Error en login:', error);
            setError(error.message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const register = async (userData) => {
        setLoading(true);
        setError(null);
        try {
            return await authAPI.register(userData);
        } catch (error) {
            setError(error.message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        //console.log('🚪 Cerrando sesión...');
        TokenManager.removeToken();
        setUser(null);
        setError(null);
    };

    const updateUserProfile = (newProfile) => {
        setUser(newProfile);
        TokenManager.setUser(newProfile);
    };

    // ✅ CORREGIDO: Usar useMemo para evitar cálculos innecesarios
    const isProfessional = useMemo(() => {
        if (!user) return false;
        
        // Múltiples formas de detectar si es profesional
        return (
            user.role === 'professional' ||
            user.role === 'accountant' || // Para compatibilidad
            user.profile?.role === 'professional' ||
            user.profile?.role === 'accountant' || // Para compatibilidad
            user.profile?.is_professional === true ||
            user.is_professional === true
        );
    }, [user]);

    const isSupervisor = useMemo(() => {
        if (!user) return false;

        return (
            user.role === 'supervisor' ||
            user.profile?.role === 'supervisor' ||
            user.profile?.is_supervisor === true ||
            user.is_supervisor === true ||
            user.role === 'manager' || // Los managers también son supervisores
            user.profile?.role === 'manager'
        );
    }, [user]);

    const isManager = useMemo(() => {
        if (!user) return false;

        return (
            user.role === 'manager' ||
            user.profile?.role === 'manager' ||
            user.profile?.is_manager === true ||
            user.is_manager === true
        );
    }, [user]);

    // ✅ CORREGIDO: Usar useMemo para getUserRole
    const getUserRole = useMemo(() => {
        if (!user) return null;

        // Prioridad: role directo > profile.role > flags booleanos
        if (user.role) return user.role;
        if (user.profile?.role) return user.profile.role;
        
        if (user.profile?.is_manager || user.is_manager) return 'manager';
        if (user.profile?.is_supervisor || user.is_supervisor) return 'supervisor';
        if (user.profile?.is_professional || user.is_professional) return 'professional';

        return 'user';
    }, [user]);

    // ✅ CORREGIDO: isAuthenticated como useMemo
    const isAuthenticated = useMemo(() => {
        return !!user && !!TokenManager.getToken();
    }, [user]);

    // ✅ CORREGIDO: Eliminar duplicados en el objeto value
    const value = {
        // Estado
        user,
        loading,
        error,
        
        // Métodos de autenticación
        login,
        register,
        logout,
        updateUserProfile,
        
        // Propiedades computadas
        isAuthenticated,
        getUserRole,
        
        // Helpers de roles
        isProfessional,
        isSupervisor,
        isManager,
        
        // ✅ Para compatibilidad: isAccountant como alias de isProfessional
        isAccountant: isProfessional, // Alias para compatibilidad
    };

    // Debug para ver qué se está retornando
    //useEffect(() => {
        //console.log('🎭 Auth Context actualizado:', {
            //user: user?.username,
            //role: getUserRole,
            //isProfessional,
            //isSupervisor,
            //isManager,
            //isAuthenticated
        //});
    //}, [user, getUserRole, isProfessional, isSupervisor, isManager, isAuthenticated]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
